import { COLORS } from '../themev2';
import { BankingCurrency } from '../types/banking';
import { filesURL } from './auth';

export const bankingCardCreationTermsAndConditionsUrl =
  'https://www.airwallex.com/us/terms/cardholder-commercial#cardholder-terms';

export const LOCK_RATE_HELP_ARTICLE_URL = 'https://ayuda.marcofi.com/en/articles/4842112';

export const statusColorMap: Record<string, object> = {
  ACTIVE: {
    background: '#E1FAED',
    color: '#11705F',
  },
  PROCESSING: {
    background: '#FCF0DB',
    color: '#AC8318',
  },
  SUSPENDED: {
    background: '#FCF0DB',
    color: '#AC8318',
  },
  ACTION_REQUIRED: {
    background: '#FEE3E3',
    color: '#930909',
  },
};

export const defaultStatusColor = { background: COLORS.gray[500], color: COLORS.gray[1200] };

export const transferMethods: Record<string, { method: string; takes: string }> = {
  ACH: {
    method: 'domesticACHTransfer',
    takes: 'takes1to2Days',
  },
  FEDWIRE: {
    method: 'domesticFedwireTransfer',
    takes: 'takes0to1Days',
  },
  SPEI: { method: 'domesticSPEITransfer', takes: 'takes0to1Days' },
  SWIFT: {
    method: 'internationalSWIFTTransfer',
    takes: 'takes0to3Days',
  },
  SEPA: { method: 'domesticSEPATransfer', takes: 'takes0to2Days' },
  SEPA_INSTANT: {
    method: 'domesticSEPAInstantTransfer',
    takes: 'takes0to1Days',
  },
  TARGET2: {
    method: 'domesticTARGET2Transfer',
    takes: 'takes0to1Days',
  },
  INTRADAGCLEARING: {
    method: 'domesticINTRADAGCLEARINGTransfer',
    takes: 'takes0to1Days',
  },
  KRONOS2: {
    method: 'domesticKRONOS2Transfer',
    takes: 'takes0to1Days',
  },
  BANK_TRANSFER: {
    method: 'domesticBankTransfer',
    takes: 'takes0to1Days',
  },
  BECS: {
    method: 'domesticBECSTransfer',
    takes: 'takes0to1Days',
  },
  RTGS: {
    method: 'domesticRTGSTransfer',
    takes: 'takes0to1Days',
  },
  FPS: {
    method: 'domesticFPSTransfer',
    takes: 'takes0to1Days',
  },
  FASTER_PAYMENTS: {
    method: 'domesticFasterPaymentsTransfer',
    takes: 'takes0to1Days',
  },
  CHAPS: {
    method: 'domesticCHAPSTransfer',
    takes: 'takes0to1Days',
  },
  BACS: {
    method: 'domesticBACSTransfer',
    takes: 'takes1to2Days',
  },
};

export const transactionsStatusFilterOptions = [
  'FAILED',
  'CLEARED',
  'PENDING',
  'SETTLED',
  'APPROVED',
  'REVERSED',
  'CANCELLED',
  'IN_REVIEW',
  'DISPATCHED',
  'PROCESSING',
  'READY_FOR_DISPATCH',
  'PAID',
];

export const transactionsTransactionTypeFilterOptions = [
  'DEPOSIT',
  'PAYMENT',
  'CONVERSION',
  'CARD_TRANSACTION',
];

export const statusAllowedForConfirmationLetter = [
  'SETTLED',
  'PROCESSING',
  'SCHEDULED',
  'PENDING',
  'PAID',
  'SENT',
];

export type BankingCountryCode =
  | 'US'
  | 'DK'
  | 'MX'
  | 'HK'
  | 'AU'
  | 'BR'
  | 'CA'
  | 'ID'
  | 'IL'
  | 'NZ'
  | 'SG'
  | 'AE'
  | 'GB';

export const mainCurrencyByCountry: Record<BankingCountryCode, BankingCurrency> = {
  US: 'USD',
  DK: 'EUR',
  MX: 'MXN',
  HK: 'HKD',
  AU: 'AUD',
  BR: 'BRL',
  CA: 'CAD',
  ID: 'IDR',
  IL: 'ILS',
  NZ: 'NZD',
  SG: 'SGD',
  AE: 'AED',
  GB: 'GBP',
};

export const conversionDefaultCurrency: Record<string, BankingCurrency> = { from: 'USD', to: 'MXN' };

export const validationErrorCodes = {
  '038': 'banking-page:transfers.createTransfer.belowMinimum',
  '037': 'banking-page:transfers.createTransfer.aboveMaximum',
};

// Backend error_code raised when a company tries to use banking without accepting the T&C
export const BANKING_NOT_STARTED_ERROR_CODE = 'banking_not_started';

// Backend error_code returned (HTTP 409) when an outgoing money movement hits an account blocked for debits
export const BANKING_DEBITS_BLOCKED_ERROR_CODE = 'banking_account_debits_blocked';

// Airwallex error_details.code when the source wallet doesn't have enough balance for the transfer
export const BALANCE_INSUFFICIENT_ERROR_CODE = 'balance_insufficient';

// Backend error_details.message when a conversion amount is below Airwallex's minimum
export const AMOUNT_BELOW_LIMIT_ERROR_MESSAGE = 'amount_below_limit';

// Airwallex error_details.source when the transfer request_id was already used
export const REQUEST_ID_ERROR_SOURCE = 'request_id';

export enum DebitsBlockedReason {
  TmSuspended = 'tm_suspended',
  Manual = 'manual',
}

export const AIRWALLEX_SCOPE_ONBOARDING = 'w:awx_action:onboarding';
export const AIRWALLEX_SCOPE_TRANSFERS_EDIT = 'w:awx_action:transfers_edit';
export const AIRWALLEX_SCOPE_RFI_VIEW = 'r:awx_action:rfi_view';
export const AIRWALLEX_SCOPE_RFI_EDIT = 'w:awx_action:rfi_edit';

export const documentUrlByCountry: Record<string, string> = {
  US: `${filesURL}/Marco_Platform_Agreement_EN_07_2024.pdf`,
  MX: `${filesURL}/Marco_Platform_Agreement_MXN_09_2025.pdf`,
  default: `${filesURL}/Marco_Platform_Agreement_EN_07_2024.pdf`,
};
